import { useMemo } from 'react';
import type { ExpenseSummary } from '../../../../types/LegalCase';

interface ExpenseCompositionBarProps {
    summary: ExpenseSummary;
}

interface Segment {
    key: string;
    label: string;
    value: number;
    formatted: string;
    color: string;
}

// Paleta cíclica para as categorias de despesa (não há cor semântica fixa por categoria como
// na pretensão; roda a paleta na ordem de maior valor). Cores coerentes com o backoffice.
const PALETTE = [
    '#3B82F6', // azul
    '#10B981', // verde
    '#F59E0B', // âmbar
    '#8B5CF6', // roxo
    '#EF4444', // vermelho
    '#06B6D4', // ciano
    '#EC4899', // rosa
    '#84CC16', // lima
    '#F97316', // laranja
    '#6B7280', // cinza
    '#14B8A6', // teal
];

const pct = (value: number, total: number): number => (total > 0 ? (value / total) * 100 : 0);

// Barra de composição do custo do caso, irmã do ClaimCompositionBar da Pretensão: uma barra
// horizontal segmentada por categoria de despesa, com o total em destaque e a legenda colorida.
// Div puro, sem lib de gráfico (mesmo padrão da pretensão). Só entram categorias com valor.
const ExpenseCompositionBar = ({ summary }: ExpenseCompositionBarProps) => {
    const segments = useMemo<Segment[]>(
        () =>
            summary.byCategory
                .filter((c) => c.total > 0)
                .map((c, i) => ({
                    key: c.category,
                    label: c.categoryFormatted,
                    value: c.total,
                    formatted: c.totalFormatted ?? '',
                    color: PALETTE[i % PALETTE.length],
                })),
        [summary],
    );

    // Sem nada a decompor (nenhuma despesa) não renderiza.
    if (segments.length === 0) return null;

    const total = summary.total || 0;

    return (
        <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>Composição do custo</span>
                <span className="font-medium text-gray-900 dark:text-white">
                    {summary.totalFormatted || 'R$ 0,00'}
                </span>
            </div>
            <div
                className="flex h-3 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-slate-700"
                role="img"
                aria-label={`Composição do custo: ${segments.map((s) => `${s.label} ${s.formatted}`).join(', ')}`}
            >
                {segments.map((s) => (
                    <div
                        key={s.key}
                        style={{ width: `${pct(s.value, total)}%`, backgroundColor: s.color }}
                        title={`${s.label}: ${s.formatted}`}
                    />
                ))}
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1">
                {segments.map((s) => (
                    <div key={s.key} className="flex items-center gap-1.5 text-xs">
                        <span className="inline-block h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                        <span className="text-gray-600 dark:text-gray-300">{s.label}</span>
                        <span className="text-gray-400 dark:text-gray-500">{s.formatted}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ExpenseCompositionBar;
